import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const DEMO = { email: "demo@prosperaplus.com.br", password: "Demo@Prospera2026" };

const Shell = ({ title, subtitle, children }) => (
  <div className="grain flex min-h-screen items-center justify-center bg-[#060D1A] px-4 py-10">
    <div className="relative z-10 w-full max-w-md fade-up">
      <Link to="/"><Logo className="mx-auto mb-8 w-64 rounded-2xl shadow-[0_20px_70px_rgba(0,0,0,.55)] sm:w-72" testid="auth-logo" /></Link>
      <div className="card-p p-6 sm:p-8">
        <h1 className="text-2xl font-semibold">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-400">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  </div>
);

const Pw = ({ value, onChange, id = "password", label = "Senha" }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="relative mt-1">
        <Input id={id} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} required className="bg-[#0B1A33] pr-10" data-testid={`${id}-input`} />
        <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-2.5 text-slate-400" data-testid={`${id}-toggle`} aria-label="Mostrar senha">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

export function Login() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const go = async (cred) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", cred);
      signIn(data);
      nav("/app");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Entrar" subtitle="Acesse sua inteligência financeira e patrimonial.">
      <form onSubmit={(e) => { e.preventDefault(); go({ email, password }); }} className="space-y-4">
        <div>
          <Label htmlFor="email">E-mail</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 bg-[#0B1A33]" data-testid="email-input" />
        </div>
        <Pw value={password} onChange={setPassword} />
        {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-btn">{busy ? "Entrando..." : "Entrar"}</Button>
      </form>
      <Button variant="outline" className="mt-3 w-full border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20" disabled={busy}
        onClick={() => go(DEMO)} data-testid="demo-login-btn"><PlayCircle className="mr-2 h-4 w-4" /> Entrar no modo demonstração</Button>
      <div className="mt-5 flex justify-between text-sm">
        <Link to="/recuperar-senha" className="text-slate-400 hover:text-white" data-testid="forgot-link">Esqueci minha senha</Link>
        <Link to="/cadastro" className="text-[#F5D77F] hover:underline" data-testid="register-link">Criar conta</Link>
      </div>
    </Shell>
  );
}

export function Register() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", accept_terms: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", f);
      signIn(data);
      toast.success("Conta criada! Seu teste grátis de 14 dias começou.");
      nav("/app");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Criar conta" subtitle="14 dias grátis. Sem cartão de crédito.">
      <form onSubmit={submit} className="space-y-4">
        <div><Label htmlFor="name">Nome</Label><Input id="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className="mt-1 bg-[#0B1A33]" data-testid="name-input" /></div>
        <div><Label htmlFor="email">E-mail</Label><Input id="email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required className="mt-1 bg-[#0B1A33]" data-testid="email-input" /></div>
        <Pw value={f.password} onChange={(v) => setF({ ...f, password: v })} label="Senha (mín. 10 caracteres, letras e números)" />
        <label className="flex items-start gap-2 text-xs text-slate-400">
          <input type="checkbox" checked={f.accept_terms} onChange={(e) => setF({ ...f, accept_terms: e.target.checked })} className="mt-0.5 accent-[#D4AF37]" data-testid="accept-terms-checkbox" />
          <span>Li e aceito os <Link to="/termos" className="text-[#F5D77F] underline" target="_blank">Termos de Uso</Link> e a <Link to="/privacidade" className="text-[#F5D77F] underline" target="_blank">Política de Privacidade</Link> (LGPD).</span>
        </label>
        {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit-btn">{busy ? "Criando..." : "Criar conta"}</Button>
      </form>
      <p className="mt-5 text-center text-sm text-slate-400">Já tem conta? <Link to="/login" className="text-[#F5D77F]" data-testid="login-link">Entrar</Link></p>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data.message);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Recuperar senha" subtitle="Enviaremos um link seguro para o seu e-mail.">
      {sent ? <p className="text-sm text-emerald-300" data-testid="forgot-success">{sent}</p> : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label htmlFor="email">E-mail</Label><Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 bg-[#0B1A33]" data-testid="email-input" /></div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit-btn">{busy ? "Enviando..." : "Enviar link"}</Button>
        </form>
      )}
      <p className="mt-5 text-center text-sm"><Link to="/login" className="text-slate-400 hover:text-white" data-testid="back-login-link">Voltar ao login</Link></p>
    </Shell>
  );
}

export function Reset() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password });
      toast.success("Senha redefinida. Faça login.");
      nav("/login");
    } catch (err) {
      setError(errMsg(err));
    }
  };
  return (
    <Shell title="Nova senha" subtitle="Defina uma nova senha para sua conta.">
      <form onSubmit={submit} className="space-y-4">
        <Pw value={password} onChange={setPassword} id="new-password" label="Nova senha (mín. 10 caracteres)" />
        {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
        <Button type="submit" className="w-full" data-testid="reset-submit-btn">Redefinir senha</Button>
      </form>
    </Shell>
  );
}
